import {
  TradingCall,
  TradingCallConfig,
  TradingStrategy,
  MarketConditions,
  StrategyLeg,
} from '../types/TradingCall';
import { ScannerData } from '../types/ScannerData';
import { logger } from '../utils/logger';

interface StrategyScore {
  strategy: string;
  score: number;
  reasoning: string;
}

export class SimpleEnhancedTradingCallEngine {
  private config: TradingCallConfig;
  private activeCallsByUnderlying: Map<string, TradingCall[]> = new Map();

  constructor(config: TradingCallConfig) {
    this.config = config;
  }

  /**
   * Generate trading calls with enhanced logic
   */
  generateTradingCalls(data: ScannerData[]): TradingCall[] {
    if (!this.config.enabled) return [];

    const calls: TradingCall[] = [];
    const groupedData = this.groupDataByUnderlying(data);

    for (const underlying of Object.keys(groupedData)) {
      const optionsData = groupedData[underlying];
      if (this.config.preferredTickers.indexOf(underlying) === -1) continue;

      // Check if we already have max calls for this underlying
      const existingCalls = this.activeCallsByUnderlying.get(underlying) || [];
      if (existingCalls.length >= this.config.riskManagement.maxCallsPerUnderlying) {
        logger.debug(`Max calls reached for ${underlying}, skipping`);
        continue;
      }

      const marketConditions = this.analyzeMarketConditions(optionsData);
      const sentiment = this.analyzeSentiment(optionsData);
      const confidence = this.calculateConfidence(optionsData, sentiment, marketConditions);

      if (confidence < this.config.sentimentThresholds.confidence) {
        logger.debug(`Confidence too low for ${underlying}: ${confidence}`);
        continue;
      }

      // Select best strategy based on market conditions
      const bestStrategy = this.selectOptimalStrategy(sentiment, marketConditions, optionsData);
      if (!bestStrategy) continue;

      // Generate call based on selected strategy
      const call = this.generateStrategyCall(underlying, optionsData, sentiment, marketConditions, confidence, bestStrategy);
      if (call) {
        calls.push(call);
        
        // Update tracking
        const updatedCalls = [...existingCalls, call];
        this.activeCallsByUnderlying.set(underlying, updatedCalls);
      }
    }

    return calls;
  }

  /**
   * Analyze market conditions
   */
  private analyzeMarketConditions(data: ScannerData[]): MarketConditions {
    const totalVolume = data.reduce((sum, item) => sum + (item.Volume || 0), 0);
    const avgVolume = totalVolume / data.length;
    const avgIV = data.reduce((sum, item) => sum + (item['Implied Volatility'] || 0), 0) / data.length;
    
    // Calculate call/put ratio
    const callVolume = data.filter(item => item['Option Type'] === 'C').reduce((sum, item) => sum + (item.Volume || 0), 0);
    const putVolume = data.filter(item => item['Option Type'] === 'P').reduce((sum, item) => sum + (item.Volume || 0), 0);
    const callPutRatio = callVolume / (putVolume || 1);
    
    // Determine volatility
    let volatility: 'LOW' | 'MEDIUM' | 'HIGH';
    if (avgIV < 0.2) volatility = 'LOW';
    else if (avgIV < 0.4) volatility = 'MEDIUM';
    else volatility = 'HIGH';

    // Determine trend
    let trend: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
    if (callPutRatio > 1.5) trend = 'BULLISH';
    else if (callPutRatio < 0.7) trend = 'BEARISH';
    else trend = 'NEUTRAL';

    // Determine momentum
    const momentum = this.calculateMomentum(data);

    // Determine volume
    let volume: 'LOW' | 'MEDIUM' | 'HIGH';
    if (avgVolume < 100) volume = 'LOW';
    else if (avgVolume < 500) volume = 'MEDIUM';
    else volume = 'HIGH';

    // Determine sentiment
    const sentiment = this.calculateOverallSentiment(data);

    return {
      volatility,
      trend,
      momentum,
      volume,
      sentiment
    };
  }

  /**
   * Analyze sentiment
   */
  private analyzeSentiment(data: ScannerData[]): string {
    const bullishCount = data.filter(item => 
      item['proper_sentiment'] === 'BULLISH' || item['proper_sentiment'] === 'Strong Bullish'
    ).length;
    const bearishCount = data.filter(item => 
      item['proper_sentiment'] === 'BEARISH' || item['proper_sentiment'] === 'Strong Bearish'
    ).length;
    const totalCount = data.length;

    if (totalCount === 0) return 'NEUTRAL';

    const bullishRatio = bullishCount / totalCount;
    if (bullishRatio > 0.6) return 'STRONG_BULLISH';
    else if (bullishRatio > 0.5) return 'BULLISH';
    else if (bullishRatio < 0.4) return 'BEARISH';
    else if (bullishRatio < 0.3) return 'STRONG_BEARISH';
    else return 'NEUTRAL';
  }

  /**
   * Select optimal strategy
   */
  private selectOptimalStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): StrategyScore | null {
    const strategies: StrategyScore[] = [];

    // High IV strategies
    if (conditions.volatility === 'HIGH') {
      if (this.config.strategies.straddles) {
        strategies.push({
          strategy: 'STRADDLE',
          score: this.scoreStraddleStrategy(sentiment, conditions, data),
          reasoning: 'High IV environment - straddle for volatility expansion'
        });
      }
      
      if (this.config.strategies.strangles) {
        strategies.push({
          strategy: 'STRANGLE',
          score: this.scoreStrangleStrategy(sentiment, conditions, data),
          reasoning: 'High IV with directional bias - strangle for defined risk'
        });
      }
    }

    // Medium IV strategies
    if (conditions.volatility === 'MEDIUM') {
      if (this.config.strategies.verticalSpreads) {
        strategies.push({
          strategy: 'VERTICAL_SPREAD',
          score: this.scoreVerticalSpreadStrategy(sentiment, conditions, data),
          reasoning: 'Medium IV - vertical spread for directional play'
        });
      }
      
      if (this.config.strategies.collars) {
        strategies.push({
          strategy: 'COLLAR',
          score: this.scoreCollarStrategy(sentiment, conditions, data),
          reasoning: 'Medium IV - collar for defined risk income'
        });
      }
    }

    // Low IV strategies
    if (conditions.volatility === 'LOW') {
      if (this.config.strategies.nakedCalls && sentiment.includes('BULLISH')) {
        strategies.push({
          strategy: 'NAKED_CALL',
          score: this.scoreNakedCallStrategy(sentiment, conditions, data),
          reasoning: 'Low IV bullish - naked call for leverage'
        });
      }
      
      if (this.config.strategies.nakedPuts && sentiment.includes('BEARISH')) {
        strategies.push({
          strategy: 'NAKED_PUT',
          score: this.scoreNakedPutStrategy(sentiment, conditions, data),
          reasoning: 'Low IV bearish - naked put for leverage'
        });
      }
    }

    // Sort by score and return best
    strategies.sort((a, b) => b.score - a.score);
    return strategies.length > 0 ? strategies[0] : null;
  }

  /**
   * Strategy scoring methods
   */
  private scoreStraddleStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // High volatility is good for straddles
    if (conditions.volatility === 'HIGH') score += 30;
    
    // Neutral sentiment is good for straddles
    if (sentiment === 'NEUTRAL') score += 25;
    
    // High volume is good
    if (conditions.volume === 'HIGH') score += 15;
    
    // Strong momentum either way is good
    if (conditions.momentum === 'STRONG') score += 20;
    
    return Math.min(score, 100);
  }

  private scoreStrangleStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // High volatility is good
    if (conditions.volatility === 'HIGH') score += 25;
    
    // Some directional bias is good
    if (sentiment !== 'NEUTRAL') score += 20;
    
    // High volume is good
    if (conditions.volume === 'HIGH') score += 15;
    
    // Strong trend is good
    if (conditions.trend !== 'NEUTRAL') score += 20;
    
    return Math.min(score, 100);
  }

  private scoreVerticalSpreadStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // Clear directional sentiment is good
    if (sentiment.includes('BULLISH') || sentiment.includes('BEARISH')) score += 30;
    
    // Medium volatility is good for spreads
    if (conditions.volatility === 'MEDIUM') score += 25;
    
    // Strong trend is good
    if (conditions.trend !== 'NEUTRAL') score += 25;
    
    // Good volume is good
    if (conditions.volume === 'MEDIUM' || conditions.volume === 'HIGH') score += 20;
    
    return Math.min(score, 100);
  }

  private scoreCollarStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // Neutral to slightly bullish is good for collars
    if (sentiment === 'NEUTRAL' || sentiment === 'BULLISH') score += 25;
    
    // Medium volatility is good
    if (conditions.volatility === 'MEDIUM') score += 25;
    
    // Low to medium volatility is good
    if (conditions.volatility === 'LOW' || conditions.volatility === 'MEDIUM') score += 20;
    
    // Good volume is good
    if (conditions.volume === 'MEDIUM' || conditions.volume === 'HIGH') score += 20;
    
    return Math.min(score, 100);
  }

  private scoreNakedCallStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // Strong bullish sentiment is required
    if (sentiment.includes('BULLISH')) score += 30;
    if (sentiment === 'STRONG_BULLISH') score += 20;
    
    // Low volatility is good for naked calls
    if (conditions.volatility === 'LOW') score += 25;
    
    // Strong trend is good
    if (conditions.trend === 'BULLISH') score += 25;
    
    return Math.min(score, 100);
  }

  private scoreNakedPutStrategy(sentiment: string, conditions: MarketConditions, data: ScannerData[]): number {
    let score = 0;
    
    // Strong bearish sentiment is required
    if (sentiment.includes('BEARISH')) score += 30;
    if (sentiment === 'STRONG_BEARISH') score += 20;
    
    // Low volatility is good for naked puts
    if (conditions.volatility === 'LOW') score += 25;
    
    // Strong trend is good
    if (conditions.trend === 'BEARISH') score += 25;
    
    return Math.min(score, 100);
  }

  /**
   * Generate strategy-specific call
   */
  private generateStrategyCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number,
    strategyScore: StrategyScore
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    
    switch (strategyScore.strategy) {
      case 'STRADDLE':
        return this.generateStraddleCall(underlying, data, sentiment, conditions, confidence);
      case 'STRANGLE':
        return this.generateStrangleCall(underlying, data, sentiment, conditions, confidence);
      case 'VERTICAL_SPREAD':
        return this.generateVerticalSpreadCall(underlying, data, sentiment, conditions, confidence);
      case 'COLLAR':
        return this.generateCollarCall(underlying, data, sentiment, conditions, confidence);
      case 'NAKED_CALL':
        return this.generateNakedCall(underlying, data, sentiment, conditions, confidence);
      case 'NAKED_PUT':
        return this.generateNakedPut(underlying, data, sentiment, conditions, confidence);
      default:
        return null;
    }
  }

  /**
   * Generate straddle call
   */
  private generateStraddleCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    const atmOptions = this.findAtTheMoneyOptions(data, currentPrice);
    
    if (atmOptions.length < 2) return null;
    
    const callOption = atmOptions.find(opt => opt['Option Type'] === 'C');
    const putOption = atmOptions.find(opt => opt['Option Type'] === 'P');
    
    if (!callOption || !putOption) return null;
    
    const callPrice = callOption.Mid || (callOption['Theoretical Value'] || 0);
    const putPrice = putOption.Mid || (putOption['Theoretical Value'] || 0);
    const totalCost = callPrice + putPrice;
    
    // Calculate break-even points
    const upperBreakEven = currentPrice + totalCost;
    const lowerBreakEven = currentPrice - totalCost;
    
    // Calculate target and stop based on volatility
    const ivMultiplier = conditions.volatility === 'HIGH' ? 1.5 : 1.0;
    const targetMove = totalCost * ivMultiplier;
    const upperTarget = currentPrice + targetMove;
    const expiryStr = String(callOption['Expiry Date'] ?? '')
      .trim()
      .split('T')[0];
    const legs: StrategyLeg[] = [
      {
        type: 'BUY',
        optionType: 'CALL',
        strike: Number(callOption.Strike),
        expiry: expiryStr,
        quantity: 1,
      },
      {
        type: 'BUY',
        optionType: 'PUT',
        strike: Number(putOption.Strike),
        expiry: expiryStr,
        quantity: 1,
      },
    ];
    /** Auto-close levels use combined option mark (premium), not underlying spot */
    const tpPrem = Math.min(totalCost * 1.65, totalCost + (upperBreakEven - lowerBreakEven) * 0.2);
    const slPrem = Math.max(0.01, totalCost * 0.42);

    const strategy: TradingStrategy = {
      type: 'STRADDLE',
      details: {
        strike: currentPrice,
        expiry: callOption['Expiry Date'],
        legs,
        optionPrice: totalCost,
        targetUnderlyingPrice: upperTarget,
        stopUnderlyingPrice: lowerBreakEven,
        maxProfit: Infinity,
        maxLoss: totalCost,
        breakEvenPoints: [lowerBreakEven, upperBreakEven],
        probabilityOfProfit: 0.3,
      },
    };

    return {
      id: `straddle_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} Straddle`,
      underlying,
      callType: 'OPTIONS',
      direction: 'BUY',
      strategy,
      entryPrice: totalCost,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(totalCost),
      confidence,
      sentiment,
      reasoning: `Straddle: Long ATM call ${callOption.Strike}C and put ${putOption.Strike}P (${expiryStr}). Debit ${totalCost.toFixed(2)}. Take-profit (combined mark) ${tpPrem.toFixed(2)}; stop (combined mark) ${slPrem.toFixed(2)}. Reference underlying band: ~${lowerBreakEven.toFixed(2)} – ~${upperBreakEven.toFixed(2)}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['straddle', 'high_iv', 'volatility_play'],
    };
  }

  /**
   * Generate strangle call
   */
  private generateStrangleCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    const otmOptions = this.findOutOfTheMoneyOptions(data, currentPrice);
    
    if (otmOptions.length < 2) return null;
    
    // Find OTM call and put
    const otmCall = otmOptions.find(opt => opt['Option Type'] === 'C' && opt.Strike > currentPrice);
    const otmPut = otmOptions.find(opt => opt['Option Type'] === 'P' && opt.Strike < currentPrice);
    
    if (!otmCall || !otmPut) return null;
    
    const callPrice = otmCall.Mid || (otmCall['Theoretical Value'] || 0);
    const putPrice = otmPut.Mid || (otmPut['Theoretical Value'] || 0);
    const totalCost = callPrice + putPrice;
    
    // Calculate break-even points
    const upperBreakEven = otmCall.Strike + totalCost;
    const lowerBreakEven = otmPut.Strike - totalCost;
    
    // Reference underlying zones (informational — exits are premium-based on the wallet row)
    const targetMove = totalCost * 1.5;
    const upperTarget = otmCall.Strike + targetMove;
    const lowerRef = otmPut.Strike - targetMove;
    const expiryStr = String(otmCall['Expiry Date'] ?? '')
      .trim()
      .split('T')[0];
    const legs: StrategyLeg[] = [
      {
        type: 'BUY',
        optionType: 'CALL',
        strike: Number(otmCall.Strike),
        expiry: expiryStr,
        quantity: 1,
      },
      {
        type: 'BUY',
        optionType: 'PUT',
        strike: Number(otmPut.Strike),
        expiry: expiryStr,
        quantity: 1,
      },
    ];
    const tpPrem = totalCost * 1.65;
    const slPrem = Math.max(0.01, totalCost * 0.42);

    const strategy: TradingStrategy = {
      type: 'STRANGLE',
      details: {
        strike: (otmCall.Strike + otmPut.Strike) / 2,
        expiry: otmCall['Expiry Date'],
        legs,
        optionPrice: totalCost,
        targetUnderlyingPrice: upperTarget,
        stopUnderlyingPrice: lowerBreakEven,
        maxProfit: Infinity,
        maxLoss: totalCost,
        breakEvenPoints: [lowerBreakEven, upperBreakEven],
        probabilityOfProfit: 0.4,
      },
    };

    return {
      id: `strangle_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} Strangle`,
      underlying,
      callType: 'OPTIONS',
      direction: 'BUY',
      strategy,
      entryPrice: totalCost,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(totalCost),
      confidence,
      sentiment,
      reasoning: `Strangle: Long ${otmCall.Strike}C and ${otmPut.Strike}P (${expiryStr}). Debit ${totalCost.toFixed(2)}. Take-profit (combined mark) ${tpPrem.toFixed(2)}; stop (combined mark) ${slPrem.toFixed(2)}. Reference move: above ~${upperTarget.toFixed(2)} or below ~${lowerRef.toFixed(2)}; break-evens ~${lowerBreakEven.toFixed(2)} / ~${upperBreakEven.toFixed(2)}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['strangle', 'otm', 'defined_risk'],
    };
  }

  /**
   * Generate vertical spread call
   */
  private generateVerticalSpreadCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    const direction = sentiment.includes('BULLISH') ? 'BULL' : 'BEAR';
    
    let longOption, shortOption;
    
    if (direction === 'BULL') {
      // Bull call spread
      const itmCall = this.findInTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'C');
      const otmCall = this.findOutOfTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'C');
      
      if (!itmCall || !otmCall) return null;
      
      longOption = itmCall;
      shortOption = otmCall;
    } else {
      // Bear put spread
      const otmPut = this.findOutOfTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'P');
      const itmPut = this.findInTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'P');
      
      if (!otmPut || !itmPut) return null;
      
      longOption = otmPut;
      shortOption = itmPut;
    }
    
    const longPrice = longOption.Mid || (longOption['Theoretical Value'] || 0);
    const shortPrice = shortOption.Mid || (shortOption['Theoretical Value'] || 0);
    const netCost = longPrice - shortPrice;
    const maxProfit = Math.abs(longOption.Strike - shortOption.Strike) - netCost;

    const refUnderlyingTarget = direction === 'BULL' ? shortOption.Strike : longOption.Strike;
    const refUnderlyingStop = direction === 'BULL' ? longOption.Strike : shortOption.Strike;
    const expStr = String(longOption['Expiry Date'] ?? '')
      .trim()
      .split('T')[0];
    const legs: StrategyLeg[] =
      direction === 'BULL'
        ? [
            {
              type: 'BUY',
              optionType: 'CALL',
              strike: Number(longOption.Strike),
              expiry: expStr,
              quantity: 1,
            },
            {
              type: 'SELL',
              optionType: 'CALL',
              strike: Number(shortOption.Strike),
              expiry: expStr,
              quantity: 1,
            },
          ]
        : [
            {
              type: 'BUY',
              optionType: 'PUT',
              strike: Number(longOption.Strike),
              expiry: expStr,
              quantity: 1,
            },
            {
              type: 'SELL',
              optionType: 'PUT',
              strike: Number(shortOption.Strike),
              expiry: expStr,
              quantity: 1,
            },
          ];

    const tpPrem = Math.min(netCost + maxProfit * 0.85, netCost * 2.5);
    const slPrem = Math.max(0.01, netCost * 0.42);

    const strategy: TradingStrategy = {
      type: 'VERTICAL_SPREAD',
      details: {
        strike: (longOption.Strike + shortOption.Strike) / 2,
        expiry: longOption['Expiry Date'],
        legs,
        optionPrice: netCost,
        targetUnderlyingPrice: refUnderlyingTarget,
        stopUnderlyingPrice: refUnderlyingStop,
        maxProfit,
        maxLoss: netCost,
        breakEvenPoints: [direction === 'BULL' ? currentPrice + netCost : currentPrice - netCost],
        probabilityOfProfit: 0.6,
      },
    };

    return {
      id: `vertical_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} ${direction} Spread`,
      underlying,
      callType: 'OPTIONS',
      direction: direction === 'BULL' ? 'BUY' : 'SELL',
      strategy,
      entryPrice: netCost,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(netCost),
      confidence,
      sentiment,
      reasoning: `${direction} spread: Long ${longOption.Strike}, short ${shortOption.Strike} (${expStr}). Net debit ${netCost.toFixed(2)}. Take-profit (spread mark) ~${tpPrem.toFixed(2)}; stop ~${slPrem.toFixed(2)}. Reference underlying: toward ~${refUnderlyingTarget} (profit zone) vs ~${refUnderlyingStop}. Max profit ${maxProfit.toFixed(2)}, max loss ${netCost.toFixed(2)}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['vertical_spread', direction.toLowerCase(), 'defined_risk']
    };
  }

  /**
   * Generate collar call
   */
  private generateCollarCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    
    // Find ATM put and OTM call
    const atmPut = this.findAtTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'P');
    const otmCall = this.findOutOfTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'C');
    
    if (!atmPut || !otmCall) return null;
    
    const putPrice = atmPut.Mid || (atmPut['Theoretical Value'] || 0);
    const callPrice = otmCall.Mid || (otmCall['Theoretical Value'] || 0);
    const netCost = putPrice - callPrice;

    const upperTarget = otmCall.Strike;
    const lowerTarget = atmPut.Strike;
    const expStr = String(atmPut['Expiry Date'] ?? '')
      .trim()
      .split('T')[0];
    const legs: StrategyLeg[] = [
      {
        type: 'BUY',
        optionType: 'PUT',
        strike: Number(atmPut.Strike),
        expiry: expStr,
        quantity: 1,
      },
      {
        type: 'SELL',
        optionType: 'CALL',
        strike: Number(otmCall.Strike),
        expiry: expStr,
        quantity: 1,
      },
    ];
    const basis = Math.abs(netCost) > 1e-6 ? Math.abs(netCost) : 0.01;
    const tpPrem = basis * 1.55;
    const slPrem = Math.max(0.01, basis * 0.42);

    const strategy: TradingStrategy = {
      type: 'COLLAR',
      details: {
        strike: currentPrice,
        expiry: atmPut['Expiry Date'],
        legs,
        optionPrice: netCost,
        targetUnderlyingPrice: upperTarget,
        stopUnderlyingPrice: lowerTarget,
        maxProfit: upperTarget - currentPrice - netCost,
        maxLoss: currentPrice - lowerTarget - netCost,
        breakEvenPoints: [currentPrice + netCost],
        probabilityOfProfit: 0.7,
      },
    };

    return {
      id: `collar_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} Collar`,
      underlying,
      callType: 'OPTIONS',
      direction: 'BUY',
      strategy,
      entryPrice: basis,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(netCost),
      confidence,
      sentiment,
      reasoning: `Collar: Long ${atmPut.Strike}P, short ${otmCall.Strike}C (${expStr}). Net ${netCost.toFixed(2)}. Take-profit (position mark) ~${tpPrem.toFixed(2)}; stop ~${slPrem.toFixed(2)}. Reference: protection near ~${lowerTarget}, cap near ~${upperTarget}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['collar', 'income', 'protection']
    };
  }

  /**
   * Generate naked call
   */
  private generateNakedCall(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    const otmCall = this.findOutOfTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'C');
    
    if (!otmCall) return null;
    
    const callPrice = otmCall.Mid || (otmCall['Theoretical Value'] || 0);
    const refUnderlyingTarget = otmCall.Strike + callPrice * 2;
    const stopPrice = currentPrice;
    const tpPrem = callPrice * 1.75;
    const slPrem = Math.max(0.01, callPrice * 0.42);

    const strategy: TradingStrategy = {
      type: 'NAKED_CALL',
      details: {
        strike: otmCall.Strike,
        expiry: otmCall['Expiry Date'],
        optionPrice: callPrice,
        targetUnderlyingPrice: refUnderlyingTarget,
        stopUnderlyingPrice: stopPrice,
        maxProfit: Infinity,
        maxLoss: callPrice,
        breakEvenPoints: [otmCall.Strike + callPrice],
        probabilityOfProfit: 0.4,
      },
    };

    return {
      id: `naked_call_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} ${otmCall.Strike} Call`,
      underlying,
      callType: 'OPTIONS',
      direction: 'BUY',
      strategy,
      entryPrice: callPrice,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(callPrice),
      confidence,
      sentiment,
      reasoning: `Long ${otmCall.Strike} call — premium ${callPrice.toFixed(2)}. Take-profit (mark) ~${tpPrem.toFixed(2)}; stop ~${slPrem.toFixed(2)}. Reference underlying above ~${refUnderlyingTarget.toFixed(2)}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['naked_call', 'bullish', 'leverage']
    };
  }

  /**
   * Generate naked put
   */
  private generateNakedPut(
    underlying: string,
    data: ScannerData[],
    sentiment: string,
    conditions: MarketConditions,
    confidence: number
  ): TradingCall | null {
    const currentPrice = data[0]?.['Underlying Price'] || 100;
    const otmPut = this.findOutOfTheMoneyOptions(data, currentPrice).find(opt => opt['Option Type'] === 'P');
    
    if (!otmPut) return null;
    
    const putPrice = otmPut.Mid || (otmPut['Theoretical Value'] || 0);
    const refUnderlyingTarget = otmPut.Strike - putPrice * 2;
    const stopPrice = currentPrice;
    const tpPrem = putPrice * 1.75;
    const slPrem = Math.max(0.01, putPrice * 0.42);

    const strategy: TradingStrategy = {
      type: 'NAKED_PUT',
      details: {
        strike: otmPut.Strike,
        expiry: otmPut['Expiry Date'],
        optionPrice: putPrice,
        targetUnderlyingPrice: refUnderlyingTarget,
        stopUnderlyingPrice: stopPrice,
        maxProfit: Infinity,
        maxLoss: putPrice,
        breakEvenPoints: [otmPut.Strike - putPrice],
        probabilityOfProfit: 0.4,
      },
    };

    return {
      id: `naked_put_${underlying}_${Date.now()}`,
      timestamp: new Date(),
      symbol: `${underlying} ${otmPut.Strike} Put`,
      underlying,
      callType: 'OPTIONS',
      direction: 'BUY',
      strategy,
      entryPrice: putPrice,
      targetPrice: tpPrem,
      stopLoss: slPrem,
      quantity: this.calculatePositionSize(putPrice),
      confidence,
      sentiment,
      reasoning: `Long ${otmPut.Strike} put — premium ${putPrice.toFixed(2)}. Take-profit (mark) ~${tpPrem.toFixed(2)}; stop ~${slPrem.toFixed(2)}. Reference underlying below ~${refUnderlyingTarget.toFixed(2)}.`,
      marketConditions: conditions,
      status: 'ACTIVE',
      tags: ['naked_put', 'bearish', 'leverage']
    };
  }

  /**
   * Helper methods
   */
  private groupDataByUnderlying(data: ScannerData[]): Record<string, ScannerData[]> {
    const grouped: Record<string, ScannerData[]> = {};
    
    data.forEach(item => {
      const underlying = item.Underlying || item.underlying_symbol || 'Unknown';
      if (!grouped[underlying]) {
        grouped[underlying] = [];
      }
      grouped[underlying].push(item);
    });
    
    return grouped;
  }

  private findAtTheMoneyOptions(data: ScannerData[], currentPrice: number): ScannerData[] {
    return data.filter(item => {
      const strike = item.Strike || 0;
      const priceDiff = Math.abs(strike - currentPrice);
      return priceDiff <= currentPrice * 0.02; // Within 2% of current price
    });
  }

  private findInTheMoneyOptions(data: ScannerData[], currentPrice: number): ScannerData[] {
    return data.filter(item => {
      const strike = item.Strike || 0;
      const optionType = item['Option Type'];
      
      if (optionType === 'C') {
        return strike < currentPrice;
      } else if (optionType === 'P') {
        return strike > currentPrice;
      }
      return false;
    });
  }

  private findOutOfTheMoneyOptions(data: ScannerData[], currentPrice: number): ScannerData[] {
    return data.filter(item => {
      const strike = item.Strike || 0;
      const optionType = item['Option Type'];
      
      if (optionType === 'C') {
        return strike > currentPrice;
      } else if (optionType === 'P') {
        return strike < currentPrice;
      }
      return false;
    });
  }

  private calculateMomentum(data: ScannerData[]): 'STRONG' | 'WEAK' | 'MIXED' {
    const bullishCount = data.filter(item => 
      item['proper_sentiment'] === 'BULLISH' || item['proper_sentiment'] === 'Strong Bullish'
    ).length;
    
    const bearishCount = data.filter(item => 
      item['proper_sentiment'] === 'BEARISH' || item['proper_sentiment'] === 'Strong Bearish'
    ).length;
    
    const totalCount = data.length;
    if (totalCount === 0) return 'MIXED';
    
    const dominantDirection = Math.max(bullishCount, bearishCount);
    const ratio = dominantDirection / totalCount;
    
    if (ratio > 0.7) return 'STRONG';
    if (ratio > 0.5) return 'WEAK';
    return 'MIXED';
  }

  private calculateOverallSentiment(data: ScannerData[]): 'BULLISH' | 'BEARISH' | 'NEUTRAL' {
    const bullishCount = data.filter(item => 
      item['proper_sentiment'] === 'BULLISH' || item['proper_sentiment'] === 'Strong Bullish'
    ).length;
    
    const bearishCount = data.filter(item => 
      item['proper_sentiment'] === 'BEARISH' || item['proper_sentiment'] === 'Strong Bearish'
    ).length;
    
    const totalCount = data.length;
    if (totalCount === 0) return 'NEUTRAL';
    
    const bullishRatio = bullishCount / totalCount;
    if (bullishRatio > 0.6) return 'BULLISH';
    if (bullishRatio < 0.4) return 'BEARISH';
    return 'NEUTRAL';
  }

  private calculateConfidence(data: ScannerData[], sentiment: string, conditions: MarketConditions): number {
    let confidence = 0;
    
    // Base confidence from sentiment strength
    if (sentiment === 'STRONG_BULLISH' || sentiment === 'STRONG_BEARISH') confidence += 30;
    else if (sentiment === 'BULLISH' || sentiment === 'BEARISH') confidence += 20;
    else confidence += 10;
    
    // Volume confidence
    if (conditions.volume === 'HIGH') confidence += 20;
    else if (conditions.volume === 'MEDIUM') confidence += 15;
    else confidence += 5;
    
    // Trend confidence
    if (conditions.trend !== 'NEUTRAL') confidence += 15;
    
    // Volatility confidence (medium is often best for options)
    if (conditions.volatility === 'MEDIUM') confidence += 15;
    else if (conditions.volatility === 'HIGH') confidence += 10;
    else confidence += 5;
    
    // Momentum confidence
    if (conditions.momentum === 'STRONG') confidence += 15;
    else if (conditions.momentum === 'WEAK') confidence += 10;
    else confidence += 5;
    
    return Math.min(confidence, 100);
  }

  private calculatePositionSize(optionPrice: number): number {
    const maxRisk = this.config.riskManagement.maxRiskPerTrade;
    const positionSize = Math.floor(maxRisk / optionPrice);
    return Math.max(1, Math.min(positionSize, this.config.riskManagement.maxPositionSize));
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<TradingCallConfig>) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get active calls by underlying
   */
  getActiveCallsByUnderlying(underlying: string): TradingCall[] {
    return this.activeCallsByUnderlying.get(underlying) || [];
  }

  /**
   * Remove call from tracking
   */
  removeCall(callId: string) {
    const underlyings = Array.from(this.activeCallsByUnderlying.keys());
    for (const underlying of underlyings) {
      const calls = this.activeCallsByUnderlying.get(underlying) || [];
      const filteredCalls = calls.filter(call => call.id !== callId);
      this.activeCallsByUnderlying.set(underlying, filteredCalls);
    }
  }
} 